import React, { useState, useEffect } from 'react';
import worksheetService from '../../services/worksheetService';

const ALL_STANDARD_FIELDS = [
  { fieldCode: 'SampleNo', fieldLabel: 'Sample / Barcode', defaultWidth: '100px', alignment: 'Left', fontSize: 11, isBold: true },
  { fieldCode: 'LabNo', fieldLabel: 'Lab No', defaultWidth: '90px', alignment: 'Left', fontSize: 11, isBold: false },
  { fieldCode: 'PatientName', fieldLabel: 'Patient Demographics', defaultWidth: '160px', alignment: 'Left', fontSize: 11, isBold: true },
  { fieldCode: 'TestName', fieldLabel: 'Investigation / Test', defaultWidth: '140px', alignment: 'Left', fontSize: 11, isBold: true },
  { fieldCode: 'ParameterName', fieldLabel: 'Parameter Name', defaultWidth: '150px', alignment: 'Left', fontSize: 11, isBold: false },
  { fieldCode: 'ResultValue', fieldLabel: 'Manual Result Entry', defaultWidth: '120px', alignment: 'Left', fontSize: 11, isBold: false },
  { fieldCode: 'Unit', fieldLabel: 'Unit', defaultWidth: '70px', alignment: 'Center', fontSize: 11, isBold: false },
  { fieldCode: 'ReferenceRange', fieldLabel: 'Biological Ref Interval', defaultWidth: '140px', alignment: 'Left', fontSize: 10, isBold: false },
  { fieldCode: 'Flag', fieldLabel: 'Critical / Flag', defaultWidth: '60px', alignment: 'Center', fontSize: 10, isBold: true },
  { fieldCode: 'Priority', fieldLabel: 'Priority', defaultWidth: '70px', alignment: 'Center', fontSize: 10, isBold: true },
  { fieldCode: 'SampleType', fieldLabel: 'Sample Container', defaultWidth: '90px', alignment: 'Left', fontSize: 10, isBold: false },
  { fieldCode: 'ReceivedDate', fieldLabel: 'Received Time', defaultWidth: '80px', alignment: 'Center', fontSize: 10, isBold: false },
  { fieldCode: 'Technician', fieldLabel: 'Bench Technician', defaultWidth: '110px', alignment: 'Left', fontSize: 10, isBold: false },
  { fieldCode: 'Analyzer', fieldLabel: 'Analyzer Instrument', defaultWidth: '110px', alignment: 'Left', fontSize: 10, isBold: false },
  { fieldCode: 'TatRemaining', fieldLabel: 'TAT Remaining', defaultWidth: '85px', alignment: 'Center', fontSize: 10, isBold: false }
];

const WorksheetTemplateBuilderModal = ({
  isOpen,
  onClose,
  templateId = null,
  onSaved
}) => {
  const [formData, setFormData] = useState({
    templateName: '',
    departmentId: 1,
    subCategoryId: null,
    layoutType: 'PatientWise',
    groupBy: 'Patient',
    sortBy: 'Priority',
    sortOrder: 'ASC',
    paperSize: 'A4',
    orientation: 'Portrait',
    margins: 'Normal',
    includeHeader: true,
    includeFooter: true,
    includeBarcode: true,
    includeSignature: true,
    isDefault: false,
    isActive: true
  });

  const [activeFields, setActiveFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load existing template if editing
  useEffect(() => {
    if (!isOpen) return;

    if (templateId) {
      setIsLoading(true);
      worksheetService
        .getTemplateById(templateId)
        .then((res) => {
          if (res.success && res.data) {
            const t = res.data;
            setFormData({
              templateName: t.templateName,
              departmentId: t.departmentId || 1,
              subCategoryId: t.subCategoryId,
              layoutType: t.layoutType,
              groupBy: t.groupBy || 'Patient',
              sortBy: t.sortBy || 'Priority',
              sortOrder: t.sortOrder || 'ASC',
              paperSize: t.paperSize || 'A4',
              orientation: t.orientation || 'Portrait',
              margins: t.margins || 'Normal',
              includeHeader: t.includeHeader ?? true,
              includeFooter: t.includeFooter ?? true,
              includeBarcode: t.includeBarcode ?? true,
              includeSignature: t.includeSignature ?? true,
              isDefault: t.isDefault,
              isActive: t.isActive
            });

            if (t.fields && t.fields.length > 0) {
              setActiveFields(
                t.fields.map((f, idx) => ({
                  fieldCode: f.fieldCode,
                  fieldLabel: f.fieldLabel,
                  width: f.width || '100px',
                  displayOrder: f.displayOrder || idx + 1,
                  visible: f.visible ?? true,
                  alignment: f.alignment || 'Left',
                  fontSize: f.fontSize || 11,
                  isBold: f.isBold ?? false
                }))
              );
            }
          }
        })
        .catch((err) => console.error('Failed to load template:', err))
        .finally(() => setIsLoading(false));
    } else {
      // Default new template initialization
      setFormData({
        templateName: 'New Custom Worksheet',
        departmentId: 1,
        subCategoryId: null,
        layoutType: 'PatientWise',
        groupBy: 'Patient',
        sortBy: 'Priority',
        sortOrder: 'ASC',
        paperSize: 'A4',
        orientation: 'Portrait',
        margins: 'Normal',
        includeHeader: true,
        includeFooter: true,
        includeBarcode: true,
        includeSignature: true,
        isDefault: false,
        isActive: true
      });

      // Default fields
      setActiveFields(
        ALL_STANDARD_FIELDS.slice(0, 8).map((f, idx) => ({
          fieldCode: f.fieldCode,
          fieldLabel: f.fieldLabel,
          width: f.defaultWidth,
          displayOrder: idx + 1,
          visible: true,
          alignment: f.alignment,
          fontSize: f.fontSize,
          isBold: f.isBold
        }))
      );
    }
  }, [isOpen, templateId]);

  if (!isOpen) return null;

  const handleAddField = (field) => {
    if (activeFields.some((f) => f.fieldCode === field.fieldCode)) {
      return; // Already added
    }
    setActiveFields((prev) => [
      ...prev,
      {
        fieldCode: field.fieldCode,
        fieldLabel: field.fieldLabel,
        width: field.defaultWidth,
        displayOrder: prev.length + 1,
        visible: true,
        alignment: field.alignment,
        fontSize: field.fontSize,
        isBold: field.isBold
      }
    ]);
  };

  const handleRemoveField = (fieldCode) => {
    setActiveFields((prev) => prev.filter((f) => f.fieldCode !== fieldCode));
  };

  const handleFieldChange = (index, prop, value) => {
    setActiveFields((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [prop]: value };
      return copy;
    });
  };

  const handleMoveField = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= activeFields.length) return;

    setActiveFields((prev) => {
      const copy = [...prev];
      const temp = copy[index];
      copy[index] = copy[targetIndex];
      copy[targetIndex] = temp;
      return copy.map((f, i) => ({ ...f, displayOrder: i + 1 }));
    });
  };

  const handleSave = async () => {
    if (!formData.templateName.trim()) {
      alert('Template Name is required.');
      return;
    }
    if (activeFields.length === 0) {
      alert('At least one field must be included in the template layout.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        templateId: templateId || undefined,
        ...formData,
        fields: activeFields.map((f, idx) => ({
          fieldCode: f.fieldCode,
          fieldLabel: f.fieldLabel,
          displayOrder: idx + 1,
          width: f.width || '100px',
          visible: f.visible,
          alignment: f.alignment || 'Left',
          fontSize: parseInt(f.fontSize, 10) || 11,
          isBold: !!f.isBold
        }))
      };

      await worksheetService.saveTemplate(payload);
      alert('Template saved successfully!');
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save template:', err);
      alert('Failed to save template: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ws-modal-overlay" onClick={onClose}>
      <div
        className="ws-modal-card"
        style={{ width: '1360px', maxWidth: '98vw', height: '94vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="ws-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
              🎨 Worksheet Template Designer &amp; Layout Engine
            </span>
            <span className="ws-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>
              {formData.layoutType}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : '💾 Save Template'}
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-sm"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Top Settings Form */}
        <div
          style={{
            padding: '12px 20px',
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: '12px',
            fontSize: '12.5px'
          }}
        >
          <div>
            <label className="ws-filter-label">Template Name *</label>
            <input
              type="text"
              className="ws-filter-input"
              value={formData.templateName}
              onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
              placeholder="e.g. Hematology Routine CBC"
            />
          </div>

          <div>
            <label className="ws-filter-label">Layout Architecture</label>
            <select
              className="ws-filter-select"
              value={formData.layoutType}
              onChange={(e) => setFormData({ ...formData, layoutType: e.target.value })}
            >
              <option value="PatientWise">Patient-Wise</option>
              <option value="TestWise">Test-Wise (Matrix)</option>
              <option value="PatientPlusTest">Patient + Test</option>
              <option value="PatientPlusParameters">Patient + Parameters</option>
              <option value="TestPlusPatients">Test + Patients</option>
              <option value="SampleWise">Sample-Wise</option>
              <option value="AnalyzerWise">Analyzer-Wise</option>
              <option value="BatchWise">Batch-Wise</option>
              <option value="Compact">Compact</option>
              <option value="Detailed">Detailed</option>
            </select>
          </div>

          <div>
            <label className="ws-filter-label">Group By</label>
            <select
              className="ws-filter-select"
              value={formData.groupBy}
              onChange={(e) => setFormData({ ...formData, groupBy: e.target.value })}
            >
              <option value="Patient">Patient</option>
              <option value="Test">Investigation / Test</option>
              <option value="Sample">Sample / Barcode</option>
              <option value="Analyzer">Analyzer Instrument</option>
              <option value="Section">Section</option>
            </select>
          </div>

          <div>
            <label className="ws-filter-label">Sort By</label>
            <select
              className="ws-filter-select"
              value={formData.sortBy}
              onChange={(e) => setFormData({ ...formData, sortBy: e.target.value })}
            >
              <option value="Priority">Priority (STAT first)</option>
              <option value="SampleNo">Sample No</option>
              <option value="LabNo">Lab No</option>
              <option value="PatientName">Patient Name</option>
              <option value="ReceivedDate">Received Date/Time</option>
            </select>
          </div>

          <div>
            <label className="ws-filter-label">Paper &amp; Orientation</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <select
                className="ws-filter-select"
                style={{ width: '50%' }}
                value={formData.paperSize}
                onChange={(e) => setFormData({ ...formData, paperSize: e.target.value })}
              >
                <option value="A4">A4</option>
                <option value="A5">A5</option>
                <option value="Letter">Letter</option>
              </select>
              <select
                className="ws-filter-select"
                style={{ width: '50%' }}
                value={formData.orientation}
                onChange={(e) => setFormData({ ...formData, orientation: e.target.value })}
              >
                <option value="Portrait">Portrait</option>
                <option value="Landscape">Landscape</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              />
              Default
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
        </div>

        {/* 3-Panel Visual Layout Builder */}
        <div className="ws-modal-body" style={{ padding: '16px', background: '#f8fafc' }}>
          <div className="ws-builder-grid">
            {/* Panel 1: Available Fields Palette */}
            <div className="ws-builder-panel">
              <div className="ws-builder-panel-header">
                <span>📋 Available Fields</span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>Click + to Add</span>
              </div>
              <div className="ws-builder-panel-body">
                {ALL_STANDARD_FIELDS.map((field) => {
                  const isAdded = activeFields.some((f) => f.fieldCode === field.fieldCode);
                  return (
                    <div
                      key={field.fieldCode}
                      className="ws-field-pill"
                      style={{ opacity: isAdded ? 0.45 : 1, cursor: isAdded ? 'default' : 'pointer' }}
                      onClick={() => !isAdded && handleAddField(field)}
                    >
                      <span>{field.fieldLabel}</span>
                      <button
                        type="button"
                        className="ws-btn ws-btn-outline ws-btn-sm"
                        disabled={isAdded}
                        style={{ padding: '2px 6px', fontSize: '12px' }}
                      >
                        {isAdded ? '✓' : '+'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Panel 2: Configured Fields Order & Formatting */}
            <div className="ws-builder-panel">
              <div className="ws-builder-panel-header">
                <span>⚙️ Active Layout Columns ({activeFields.length})</span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>Reorder &amp; Format</span>
              </div>
              <div className="ws-builder-panel-body">
                {activeFields.map((field, idx) => (
                  <div
                    key={field.fieldCode}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <button
                        type="button"
                        className="ws-btn ws-btn-outline ws-btn-sm"
                        style={{ padding: '1px 4px', fontSize: '10px' }}
                        disabled={idx === 0}
                        onClick={() => handleMoveField(idx, -1)}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        className="ws-btn ws-btn-outline ws-btn-sm"
                        style={{ padding: '1px 4px', fontSize: '10px' }}
                        disabled={idx === activeFields.length - 1}
                        onClick={() => handleMoveField(idx, 1)}
                      >
                        ▼
                      </button>
                    </div>

                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        className="ws-filter-input"
                        style={{ width: '100%', padding: '4px 6px', fontSize: '12px' }}
                        value={field.fieldLabel}
                        onChange={(e) => handleFieldChange(idx, 'fieldLabel', e.target.value)}
                        placeholder="Column Header Label"
                      />
                    </div>

                    <div style={{ width: '65px' }}>
                      <input
                        type="text"
                        className="ws-filter-input"
                        style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                        value={field.width}
                        onChange={(e) => handleFieldChange(idx, 'width', e.target.value)}
                        placeholder="Width"
                      />
                    </div>

                    <div style={{ width: '70px' }}>
                      <select
                        className="ws-filter-select"
                        style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                        value={field.alignment}
                        onChange={(e) => handleFieldChange(idx, 'alignment', e.target.value)}
                      >
                        <option value="Left">Left</option>
                        <option value="Center">Center</option>
                        <option value="Right">Right</option>
                      </select>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={field.isBold}
                        onChange={(e) => handleFieldChange(idx, 'isBold', e.target.checked)}
                      />
                      <strong style={{ fontSize: '11px' }}>B</strong>
                    </label>

                    <button
                      type="button"
                      className="ws-btn ws-btn-outline ws-btn-sm"
                      style={{ color: '#dc2626' }}
                      onClick={() => handleRemoveField(field.fieldCode)}
                      title="Remove column"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Panel 3: Live Instant Preview */}
            <div className="ws-builder-panel">
              <div className="ws-builder-panel-header">
                <span>👁️ Live Layout Preview</span>
                <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>● Instant Sync</span>
              </div>
              <div className="ws-builder-panel-body" style={{ background: '#525659', padding: '12px' }}>
                <div
                  style={{
                    background: '#ffffff',
                    padding: '14px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    fontSize: '10px'
                  }}
                >
                  <div style={{ fontWeight: 800, borderBottom: '1px solid #000', paddingBottom: '4px', marginBottom: '8px' }}>
                    {formData.templateName || 'WORKSHEET PREVIEW'}
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5px' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #000' }}>
                        {activeFields.map((f) => (
                          <th
                            key={f.fieldCode}
                            style={{
                              padding: '4px',
                              textAlign: f.alignment.toLowerCase(),
                              fontWeight: f.isBold ? 800 : 600,
                              borderRight: '1px solid #e2e8f0'
                            }}
                          >
                            {f.fieldLabel}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {activeFields.map((f) => (
                          <td
                            key={f.fieldCode}
                            style={{
                              padding: '4px',
                              textAlign: f.alignment.toLowerCase(),
                              borderRight: '1px solid #f1f5f9',
                              borderBottom: '1px solid #f1f5f9',
                              color: '#475569'
                            }}
                          >
                            {f.fieldCode === 'SampleNo' ? 'S10251' : f.fieldCode === 'PatientName' ? 'Rahul Sharma' : f.fieldCode === 'ResultValue' ? '______' : 'Sample'}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorksheetTemplateBuilderModal;
